import type {
  ApplyResetPasswordRequest,
  ChangePasswordPayload,
  ConnectRequest,
  ConnectResponse,
  LoginRequest,
  RegisterRequest,
  ResetPasswordPayload,
  TokenResponse,
} from "./model"
import { updateTokens } from "./slice"
import { withRefreshLock } from "./refreshLock"
import baseApi from "@/base/baseApi"

/**
 * Stores the tokens a response carries, under the lock refreshes take.
 *
 * Every one of these replaces the whole session, and the store is shared between tabs and
 * last-writer-wins, so the writes have to be ordered against a refresh happening elsewhere. That
 * works because the refresh side holds the lock across its whole exchange: this write therefore
 * lands either before that request starts or after its write, never in between.
 *
 * Only around the write, and deliberately not across `queryFulfilled` as well. These requests are
 * authenticated, so an expired JWT sends them through the automatic refresh — which reaches for
 * this very lock. Holding it while waiting for the response would be a circular wait with no way
 * out for either side.
 */
const storeTokens = async (
  dispatch: (action: ReturnType<typeof updateTokens>) => unknown,
  queryFulfilled: PromiseLike<{ data: TokenResponse }>,
) => {
  let response

  try {
    response = await queryFulfilled
  } catch {
    return
  }

  await withRefreshLock(() => {
    dispatch(updateTokens(response.data))
  })
}

const api = baseApi.injectEndpoints({
  endpoints: (build) => ({
    login: build.mutation<TokenResponse, LoginRequest>({
      query: (body) => ({
        url: "security/login",
        method: "POST",
        body,
      }),
      extraOptions: { withoutAuth: true },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await storeTokens(dispatch, queryFulfilled)
      },
    }),
    connectOauth: build.mutation<ConnectResponse, ConnectRequest>({
      query: ({ provider, ...body }) => ({
        url: `oauth/connect/${provider}`,
        method: "POST",
        body,
      }),
      extraOptions: { withoutAuth: true },
    }),
    register: build.mutation<TokenResponse, RegisterRequest>({
      query: (body) => ({
        url: "register",
        method: "POST",
        body,
      }),
      extraOptions: { withoutAuth: true },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await storeTokens(dispatch, queryFulfilled)
      },
    }),
    requestResetPassword: build.mutation<void, ResetPasswordPayload>({
      query: (body) => ({
        url: "request-reset-password",
        method: "POST",
        body,
      }),
      extraOptions: { withoutAuth: true },
    }),
    applyResetPassword: build.mutation<
      TokenResponse,
      ApplyResetPasswordRequest
    >({
      query: (body) => ({
        url: "apply-reset-password",
        method: "POST",
        body,
      }),
      extraOptions: { withoutAuth: true },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await storeTokens(dispatch, queryFulfilled)
      },
    }),
    applyVerifyEmail: build.mutation<void, Record<string, string>>({
      query: (params) => ({
        url: "verify-email",
        method: "POST",
        params,
      }),
      extraOptions: { withoutAuth: true },
    }),
    changePassword: build.mutation<TokenResponse, ChangePasswordPayload>({
      query: (body) => ({
        url: "change-password",
        method: "POST",
        body,
      }),
      // Changing the password revokes every session, this one included, so the response carries
      // its replacement. Without storing it the tab that just changed the password is the one that
      // gets signed out.
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await storeTokens(dispatch, queryFulfilled)
      },
    }),
    logout: build.mutation<void, string>({
      query: (payload) => ({
        url: "security/invalidate",
        method: "POST",
        body: { refreshToken: payload },
      }),
    }),
  }),
})

export const {
  useLoginMutation,
  useConnectOauthMutation,
  useRegisterMutation,
  useRequestResetPasswordMutation,
  useApplyResetPasswordMutation,
  useApplyVerifyEmailMutation,
  useChangePasswordMutation,
  useLogoutMutation,
} = api
